// src/App.js
import React, { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import SignIn from "./SignIn";
import Home from "./pages/Home";
import Converters from "./pages/Converters";
import Electronics from "./pages/Electronics";
import Favorites from "./pages/Favorites";
import Settings from "./pages/Settings";
import BuyingLists from "./pages/BuyingLists";
import NotFound from "./pages/NotFound";
import Calculator from "./pages/Calculator";
import SharedLists from "./pages/SharedLists";
import ContactUs from "./pages/ContactUs";
import Subscription from "./pages/Subscription";
import SellRequests from "./pages/SellRequests";
import ProtectedLayout from "./components/ProtectedLayout";
import ScrollToTop from "./components/ScrollToTop";
import { auth, __FIREBASE_CONFIG_OK__ } from "./lib/firebase";
import { onAuthStateChanged } from "firebase/auth";

// ⬇️ role/permissions providers
import { RoleProvider } from "./context/RoleContext";
import { UserProvider, useUser } from "./context/UserProvider";
import { ThemeProvider } from "./context/ThemeContext";

// Route guard using permissions → permissions/{role}.navigation.calculator
function RequirePriv({ children }) {
  const u = useUser();
  const ok = !!(u?.gates?.navigation?.calculator);
  return ok ? children : <Navigate to="/" replace />;
}

function LoadingScreen() {
  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center"
      style={{
        background: 'linear-gradient(135deg, #0f3a30 0%, #1a4d40 50%, #0f3a30 100%)',
      }}
    >
      {/* Animated background circles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          className="absolute w-96 h-96 rounded-full opacity-10 animate-pulse"
          style={{
            background: 'radial-gradient(circle, #4caf50 0%, transparent 70%)',
            top: '10%',
            left: '10%',
            animationDuration: '3s',
          }}
        />
        <div
          className="absolute w-64 h-64 rounded-full opacity-10 animate-pulse"
          style={{
            background: 'radial-gradient(circle, #69f0ae 0%, transparent 70%)',
            bottom: '20%',
            right: '15%',
            animationDuration: '4s',
            animationDelay: '1s',
          }}
        />
      </div>

      {/* Logo Container */}
      <div className="relative mb-8">
        {/* Outer ring - rotating */}
        <div
          className="absolute rounded-full animate-spin"
          style={{
            width: '120px',
            height: '120px',
            border: '3px solid transparent',
            borderTopColor: '#4caf50',
            borderRightColor: '#4caf50',
            animationDuration: '1.5s',
            top: '-10px',
            left: '-10px',
          }}
        />

        {/* Middle ring */}
        <div
          className="absolute rounded-full"
          style={{
            width: '100px',
            height: '100px',
            border: '2px solid rgba(255,255,255,0.1)',
          }}
        />

        {/* Logo/Icon */}
        <div
          className="relative flex items-center justify-center rounded-full"
          style={{
            width: '100px',
            height: '100px',
            background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.2) 0%, rgba(46, 125, 50, 0.3) 100%)',
            boxShadow: '0 8px 32px rgba(76, 175, 80, 0.3), inset 0 0 32px rgba(76, 175, 80, 0.1)',
          }}
        >
          <span
            className="text-3xl font-bold"
            style={{
              background: 'linear-gradient(135deg, #4caf50 0%, #69f0ae 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            N
          </span>
        </div>
      </div>

      {/* Loading text */}
      <div className="text-center relative z-10">
        <h2 className="text-lg font-medium mb-2 text-green-100">Loading...</h2>

        {/* Animated dots */}
        <div className="flex items-center justify-center gap-1.5">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="w-2 h-2 rounded-full animate-bounce"
              style={{
                backgroundColor: '#4caf50',
                animationDelay: `${i * 0.15}s`,
                animationDuration: '0.6s',
              }}
            />
          ))}
        </div>
      </div>

      {/* Progress bar */}
      <div
        className="absolute bottom-0 left-0 right-0 h-1 overflow-hidden"
        style={{ backgroundColor: 'rgba(255,255,255,0.05)' }}
      >
        <div
          className="h-full"
          style={{
            background: 'linear-gradient(90deg, transparent, #4caf50, transparent)',
            width: '30%',
            animation: 'loading-bar 1.5s ease-in-out infinite',
          }}
        />
      </div>

      <style>{`
        @keyframes loading-bar {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(400%); }
        }
      `}</style>
    </div>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [authReady, setAuthReady] = useState(false);
  const basename = process.env.NODE_ENV === 'development' ? '/' : (process.env.PUBLIC_URL || '/');

  useEffect(() => {
    if (!__FIREBASE_CONFIG_OK__) return;
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setAuthReady(true);
    });
    return () => unsubscribe();
  }, []);

  if (!__FIREBASE_CONFIG_OK__) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-slate-50">
        <div className="max-w-lg w-full rounded-xl border border-amber-300 bg-amber-50 p-5 text-amber-900">
          <h2 className="text-lg font-semibold mb-2">Firebase not configured</h2>
          <p className="text-sm">
            Add the required <code>REACT_APP_*</code> keys to <code>.env.local</code> and restart <code>npm start</code>.
          </p>
          <ul className="mt-3 text-sm list-disc pl-5">
            <li>REACT_APP_FB_API_KEY</li>
            <li>REACT_APP_FB_AUTH_DOMAIN</li>
            <li>REACT_APP_FB_PROJECT_ID</li>
            <li>REACT_APP_FB_APP_ID</li>
            <li>REACT_APP_FB_MESSAGING_SENDER_ID</li>
          </ul>
        </div>
      </div>
    );
  }

  // While checking auth state, show an elegant loading screen instead of flashing Sign In
  if (!authReady) {
    return <LoadingScreen />;
  }

  return (
    // ⬇️ Wrap the entire app so useRole() and useUser() are always inside providers
    <ThemeProvider>
      <RoleProvider>
        <UserProvider>
          <BrowserRouter basename={basename}>
            <ScrollToTop />
            <Routes>
              {/* Public */}
              <Route path="/signin" element={user ? <Navigate to="/" replace /> : <SignIn />} />

              {/* Protected + Layout */}
              <Route element={user ? <ProtectedLayout /> : <Navigate to="/signin" replace />}>
                <Route index element={<Home />} />
                <Route path="converters" element={<Converters />} />
                <Route path="electronics" element={<Electronics />} />
                <Route path="favorites" element={<Favorites />} />
                <Route path="buying-lists" element={<BuyingLists />} />
                <Route path="shared-lists" element={<SharedLists />} />
                <Route path="settings" element={<Settings />} />
                <Route path="subscription" element={<Subscription />} />
                <Route path="contact-us" element={<ContactUs />} />
                <Route path="sell-requests" element={<SellRequests />} />
                <Route path="calculator" element={<RequirePriv><Calculator /></RequirePriv>} />
              </Route>

              {/* 404 */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </BrowserRouter>
        </UserProvider>
      </RoleProvider>
    </ThemeProvider>
  );
}

export default App;
